import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import { Typography, Box } from '@mui/material';
import Breadcrumb from 'src/components/Breadcrumb';
import { useDispatch, useSelector } from 'react-redux';
import DownloadButton from 'src/components/DownloadButton';
import AddButton from 'src/components/AddButton/AddButton';
import SearchBar from 'src/components/SearchBar';
import useDebounce from 'src/hooks/useDebounce';
import {
  loadCardMovements,
  downloadCardMovements,
  sendCreditCardPaymentLinkAction,
  CLEAR_CARD_MOVEMENTS_OPTIONS
} from 'src/store/actions/cardMovementsActions';
import { selectCardMovementsQueryOptions } from 'src/store/selectors/cardMovementsSelectors';
import CreditCardPaymentForm from './CreditCardPaymentForm/index';
import useStyles from './styles';

const Toolbar = ({ className, ...rest }) => {
  const { t } = useTranslation();
  const { classes } = useStyles();
  const dispatch = useDispatch();

  const queryOptions = useSelector(selectCardMovementsQueryOptions);
  const [searchValue, setSearchValue] = useState('');
  const [openPaymentForm, setOpenPaymentForm] = useState(false);

  const debounceSearch = useDebounce(
    (clientName, queryOpts) =>
      dispatch(loadCardMovements({ ...queryOpts, clientName, page: 0 })),
    400
  );

  const handleSearch = useCallback(
    e => {
      const { value } = e.target;
      setSearchValue(value);
      debounceSearch(value, queryOptions);
    },
    [setSearchValue, debounceSearch, queryOptions]
  );

  const handleExcelExport = useCallback(() => {
    dispatch(downloadCardMovements());
  }, [dispatch]);

  const handleOpenPaymentForm = useCallback(() => {
    setOpenPaymentForm(true);
  }, []);

  const handleClosePaymentForm = useCallback(() => {
    setOpenPaymentForm(false);
  }, []);

  const handleSubmitPaymentForm = useCallback(
    (values, closeCallback) => {
      dispatch(sendCreditCardPaymentLinkAction(values, closeCallback));
    },
    [dispatch]
  );

  useEffect(() => {
    dispatch(loadCardMovements());
    return () => dispatch({ type: CLEAR_CARD_MOVEMENTS_OPTIONS });
  }, []);

  return (
    <>
      <Box className={clsx(classes.root, className)} {...rest}>
        <Breadcrumb />
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}
        >
          <Typography
            style={{ fontWeight: '400' }}
            color="primary"
            variant="h1"
          >
            {t('NAVIGATION.CARD_MOVEMENTS')}
          </Typography>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-end'
            }}
          >
            <SearchBar
              handleSearch={handleSearch}
              searchValue={searchValue}
              placeholder={t('CARD_MOVEMENTS.SEARCH_BY_CLIENT')}
            />
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                marginLeft: 4
              }}
            >
              <AddButton
                title="CARD_MOVEMENTS.GENERATE_ONE_TIME_PAYMENT"
                onClick={handleOpenPaymentForm}
                className={classes.buttons}
                tooltip={t('CARD_MOVEMENTS.GENERATE_ONE_TIME_PAYMENT')}
              />
              <DownloadButton
                onDownload={handleExcelExport}
                title="COMMON.DOWNLOAD_EXCEL"
              />
            </Box>
          </Box>
        </Box>
      </Box>
      <CreditCardPaymentForm
        open={openPaymentForm}
        handleClose={handleClosePaymentForm}
        onSubmit={handleSubmitPaymentForm}
      />
    </>
  );
};

Toolbar.propTypes = {
  className: PropTypes.string
};

export default Toolbar;
